import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiClock, FiImage, FiMapPin, FiUsers, FiLogOut, 
  FiCamera, FiHome, FiMenu, FiX 
} from 'react-icons/fi';
import HoursManager from './admin/HoursManager';
import LogoManager from './admin/LogoManager';
import LocationManager from './admin/LocationManager';
import ArtistManager from './admin/ArtistManager';
import GalleryManager from './admin/GalleryManager';
import HeroImageManager from './admin/HeroImageManager';

const AdminDashboard = ({ onLogout }) => {
  const [activeTab, setActiveTab] = useState('hours');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const tabs = [
    { id: 'hours', label: 'Hours', icon: FiClock },
    { id: 'logo', label: 'Logo', icon: FiImage },
    { id: 'location', label: 'Location', icon: FiMapPin },
    { id: 'artists', label: 'Artists', icon: FiUsers },
    { id: 'gallery', label: 'Gallery', icon: FiCamera },
    { id: 'hero', label: 'Hero Image', icon: FiHome },
  ];

  const renderContent = () => {
    switch (activeTab) {
      case 'hours':
        return <HoursManager />;
      case 'logo':
        return <LogoManager />;
      case 'location':
        return <LocationManager />;
      case 'artists':
        return <ArtistManager />;
      case 'gallery':
        return <GalleryManager />;
      case 'hero':
        return <HeroImageManager />;
      default:
        return null;
    }
  };

  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="bg-black/30 backdrop-blur-lg border-b border-white/10 sticky top-0 z-50">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex justify-between items-center">
            <h1 className="text-lg sm:text-2xl font-bold text-white tracking-tight">
              Admin Dashboard
            </h1>
            
            <div className="flex items-center gap-2 sm:gap-4">
              {/* Mobile Menu Button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden flex items-center gap-2 px-3 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-all"
              >
                {mobileMenuOpen ? <FiX className="text-xl" /> : <FiMenu className="text-xl" />}
              </button>

              {/* Logout Button */}
              <button
                onClick={onLogout}
                className="flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded-lg transition-all duration-200 text-sm sm:text-base"
              >
                <FiLogOut className="text-sm sm:text-base" />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 sm:px-6 py-4 sm:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6">
          {/* Desktop Sidebar */}
          <div className="hidden lg:block lg:col-span-3">
            <nav className="bg-black/20 backdrop-blur-lg rounded-2xl p-4 border border-white/10 sticky top-24">
              <div className="space-y-2">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                        activeTab === tab.id
                          ? 'bg-white text-slate-900 shadow-lg shadow-white/20'
                          : 'text-white/70 hover:bg-white/10 hover:text-white'
                      }`}
                    >
                      <Icon className="text-xl" />
                      <span className="font-medium">{tab.label}</span>
                    </button>
                  );
                })}
              </div>
            </nav>
          </div>

          {/* Mobile Menu */}
          <AnimatePresence>
            {mobileMenuOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="lg:hidden col-span-1 overflow-hidden"
              >
                <nav className="bg-black/20 backdrop-blur-lg rounded-2xl p-3 border border-white/10 mb-4">
                  <div className="grid grid-cols-2 gap-2">
                    {tabs.map((tab) => {
                      const Icon = tab.icon;
                      return (
                        <button
                          key={tab.id}
                          onClick={() => handleTabClick(tab.id)}
                          className={`flex items-center gap-2 px-3 py-3 rounded-xl transition-all duration-200 text-sm ${
                            activeTab === tab.id
                              ? 'bg-white text-slate-900 shadow-lg'
                              : 'text-white/70 bg-white/5 hover:bg-white/10 hover:text-white'
                          }`}
                        >
                          <Icon className="text-lg" />
                          <span className="font-medium">{tab.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </nav>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Main Content */}
          <div className="lg:col-span-9">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.2 }}
                className="bg-black/20 backdrop-blur-lg rounded-2xl p-4 sm:p-6 border border-white/10"
              >
                {renderContent()}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;